import React from "react";
import { useTranslation } from "react-i18next";

import { SplitOption } from "./types";
import {
    Card,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Toggle,
    SmallToggle,
    LanguageTabs,
    LanguageTab,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
    VisibilityButton,
    ResetButton,
    ResetAllButton,
    MethodSection,
    MethodSectionHeader,
    MethodIconCircle,
    MethodName,
    DisabledOverlay,
} from "./styled";
import { ResetIcon } from "@components/Common/SvgIcons/ResetIcon";
import { EyeIcon } from "@components/Common/SvgIcons/EyeIcon";
import { EyeOffIcon } from "@components/Common/SvgIcons/EyeOffIcon";
import { ICON_SIZE_SMALL } from "../shared/constants";

type SplitOptionsCardProps = {
    mainColor: string;
    languages: Array<{ code: string; label: string }>;
    selectedSplitLang: string;
    onSplitLanguageChange: (lang: string) => void;
    splitPaymentEnabled: boolean;
    onSplitPaymentEnabledToggle: () => void;
    splitOptions: SplitOption[];
    defaultSplitOptions: SplitOption[];
    splitDescVisible: Record<string, boolean>;
    onToggleSplitDescVisible: (id: string) => void;
    onToggleSplitOption: (id: string) => void;
    onSplitOptionTitleChange: (id: string, value: string) => void;
    onSplitOptionDescChange: (id: string, value: string) => void;
    onResetSplitTitle: (id: string) => void;
    onResetSplitDesc: (id: string) => void;
    onResetAll: () => void;
};

export function SplitOptionsCard({
    mainColor,
    languages,
    selectedSplitLang,
    onSplitLanguageChange,
    splitPaymentEnabled,
    onSplitPaymentEnabledToggle,
    splitOptions,
    defaultSplitOptions,
    splitDescVisible,
    onToggleSplitDescVisible,
    onToggleSplitOption,
    onSplitOptionTitleChange,
    onSplitOptionDescChange,
    onResetSplitTitle,
    onResetSplitDesc,
    onResetAll,
}: SplitOptionsCardProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card data-testid="scan-to-payment-split-card">
            <SectionHeader>
                <SectionTitle>{t("Split payment")}</SectionTitle>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                    }}
                >
                    <ResetAllButton
                        type="button"
                        data-testid="scan-to-split-payment-reset"
                        mainColor={mainColor}
                        onClick={onResetAll}
                        title={t("Reset")}
                        aria-label={t("Reset")}
                    >
                        <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                    </ResetAllButton>
                    <ToggleContainer>
                        <ToggleLabel>{t("Share the bill")}</ToggleLabel>
                        <Toggle
                            data-testid="scan-to-payment-toggle-split"
                            isActive={splitPaymentEnabled}
                            mainColor={mainColor}
                            onClick={onSplitPaymentEnabledToggle}
                            aria-label={t("Share the bill")}
                        />
                    </ToggleContainer>
                </div>
            </SectionHeader>

            {splitPaymentEnabled ? (
                <>
                    <SectionTitle style={{ marginBottom: "15px" }}>
                        {t("Title and Description")}
                    </SectionTitle>

                    <LanguageTabs data-testid="scan-to-payment-split-language-tabs">
                        {languages.map((lang) => (
                            <LanguageTab
                                key={lang.code}
                                data-testid={`scan-to-payment-split-lang-${lang.code}`}
                                isActive={selectedSplitLang === lang.code}
                                mainColor={mainColor}
                                onClick={() => onSplitLanguageChange(lang.code)}
                            >
                                {lang.label}
                            </LanguageTab>
                        ))}
                    </LanguageTabs>

                    <div
                        style={{
                            borderTop: "1px solid #e0e0e0",
                            paddingTop: "10px",
                        }}
                    >
                        {splitOptions.map((option) => {
                            const def = defaultSplitOptions.find(
                                (d) => d.id === option.id
                            )!;
                            return (
                                <MethodSection
                                    key={option.id}
                                    data-testid={`scan-to-payment-split-section-${option.id}`}
                                >
                                    <MethodSectionHeader>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 10,
                                            }}
                                        >
                                            <MethodIconCircle
                                                mainColor={mainColor}
                                            >
                                                <svg viewBox="0 0 24 24">
                                                    <path d={option.icon} />
                                                </svg>
                                            </MethodIconCircle>
                                            <MethodName>
                                                {def.title[selectedSplitLang] ||
                                                    def.title.fr}
                                            </MethodName>
                                        </div>
                                        <SmallToggle
                                            data-testid={`scan-to-payment-toggle-split-${option.id}`}
                                            isActive={option.enabled}
                                            mainColor={mainColor}
                                            onClick={() =>
                                                onToggleSplitOption(option.id)
                                            }
                                            aria-label={t(
                                                "Toggle split option"
                                            )}
                                        />
                                    </MethodSectionHeader>

                                    {/* Column headers */}
                                    <InputRow
                                        style={{
                                            borderBottom: "1px solid #e0e0e0",
                                            paddingBottom: "10px",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <InputLabel></InputLabel>
                                        <InputLabel style={{ fontWeight: 500 }}>
                                            {t("Default text")}
                                        </InputLabel>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                width: "100%",
                                            }}
                                        >
                                            <InputLabel
                                                style={{ fontWeight: 500 }}
                                            >
                                                {t("Customized")}
                                            </InputLabel>
                                            <ResetAllButton
                                                data-testid={`scan-to-payment-reset-split-${option.id}`}
                                                mainColor={mainColor}
                                                onClick={() => {
                                                    onResetSplitTitle(
                                                        option.id
                                                    );
                                                    onResetSplitDesc(option.id);
                                                }}
                                                title={t("Reset")}
                                                aria-label={t("Reset")}
                                            >
                                                <ResetIcon
                                                    style={{
                                                        width: ICON_SIZE_SMALL,
                                                        height: ICON_SIZE_SMALL,
                                                    }}
                                                />
                                            </ResetAllButton>
                                        </div>
                                    </InputRow>

                                    {/* Title row */}
                                    <InputRow>
                                        <InputLabel>{t("Title")}</InputLabel>
                                        <Input
                                            data-testid={`scan-to-payment-split-default-title-${option.id}`}
                                            value={
                                                def.title[selectedSplitLang] ||
                                                def.title.fr ||
                                                ""
                                            }
                                            readOnly
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                                cursor: "not-allowed",
                                            }}
                                        />
                                        <InputWrapper>
                                            <Input
                                                data-testid={`scan-to-payment-split-custom-title-${option.id}`}
                                                value={
                                                    option.title[
                                                        selectedSplitLang
                                                    ] || ""
                                                }
                                                onChange={(e) =>
                                                    onSplitOptionTitleChange(
                                                        option.id,
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={
                                                    def.title[
                                                        selectedSplitLang
                                                    ] ||
                                                    def.title.fr ||
                                                    ""
                                                }
                                            />
                                            <ResetButton
                                                data-testid={`scan-to-payment-reset-split-title-${option.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetSplitTitle(option.id)
                                                }
                                                title={t("Reset")}
                                                aria-label={t("Reset")}
                                            >
                                                <ResetIcon
                                                    style={{
                                                        width: ICON_SIZE_SMALL,
                                                        height: ICON_SIZE_SMALL,
                                                    }}
                                                />
                                            </ResetButton>
                                        </InputWrapper>
                                    </InputRow>

                                    {/* Description row */}
                                    <InputRow>
                                        <InputLabel>
                                            {t("Description")}
                                        </InputLabel>
                                        <Input
                                            data-testid={`scan-to-payment-split-default-desc-${option.id}`}
                                            value={
                                                def.description[
                                                    selectedSplitLang
                                                ] ||
                                                def.description.fr ||
                                                ""
                                            }
                                            readOnly
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                                cursor: "not-allowed",
                                            }}
                                        />
                                        <InputWrapper>
                                            <Input
                                                data-testid={`scan-to-payment-split-custom-desc-${option.id}`}
                                                value={
                                                    option.description[
                                                        selectedSplitLang
                                                    ] || ""
                                                }
                                                onChange={(e) =>
                                                    onSplitOptionDescChange(
                                                        option.id,
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={
                                                    def.description[
                                                        selectedSplitLang
                                                    ] ||
                                                    def.description.fr ||
                                                    ""
                                                }
                                            />
                                            <VisibilityButton
                                                data-testid={`scan-to-payment-visibility-split-desc-${option.id}`}
                                                isVisible={
                                                    splitDescVisible[
                                                        option.id
                                                    ] !== false
                                                }
                                                onClick={() =>
                                                    onToggleSplitDescVisible(
                                                        option.id
                                                    )
                                                }
                                                title={
                                                    splitDescVisible[
                                                        option.id
                                                    ] !== false
                                                        ? t("Hide")
                                                        : t("Show")
                                                }
                                                aria-label={
                                                    splitDescVisible[
                                                        option.id
                                                    ] !== false
                                                        ? t("Hide")
                                                        : t("Show")
                                                }
                                                aria-pressed={
                                                    splitDescVisible[
                                                        option.id
                                                    ] !== false
                                                }
                                            >
                                                {splitDescVisible[option.id] !==
                                                false ? (
                                                    <EyeIcon />
                                                ) : (
                                                    <EyeOffIcon />
                                                )}
                                            </VisibilityButton>
                                            <ResetButton
                                                data-testid={`scan-to-payment-reset-split-desc-${option.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetSplitDesc(option.id)
                                                }
                                                title={t("Reset")}
                                                aria-label={t("Reset")}
                                            >
                                                <ResetIcon
                                                    style={{
                                                        width: ICON_SIZE_SMALL,
                                                        height: ICON_SIZE_SMALL,
                                                    }}
                                                />
                                            </ResetButton>
                                        </InputWrapper>
                                    </InputRow>
                                </MethodSection>
                            );
                        })}
                    </div>
                </>
            ) : (
                <DisabledOverlay>
                    {t("All actions are disabled. The section will be hidden.")}
                </DisabledOverlay>
            )}
        </Card>
    );
}
